<template>
	<div
		class="shadow-md border border-border-tertiary rounded-2.5xl relative grid grid-rows-[auto_1fr_auto]"
	>
		<div class="mb-4 relative">
			<div class="pt-2 sm:pt-4">
				<div
					v-if="outOfStock"
					class="absolute left-0 right-0 top-[50%] translate-y-[-50%] z-20 text-center bg-background-secondary text-body-secondary p-2 font-bold typo-body-xl"
				>
					Vyprodáno
				</div>
				<component
					:is="hasDetail ? AbsoluteInternalLink : 'div' "
					:to="link"
					class="relative block px-2 sm:px-5"
					:class="isMenuTile
						? 'rem:h-[102px] md:rem:h-[150px]'
						: 'rem:h-[102px] md:rem:h-[150px] min-[1440px]:rem:h-[85px] min-[1600px]:rem:h-[120px] xxl:rem:h-[150px]'
					"
					tabindex="-1"
				>
					<NuxtImg
						v-if="product.meal?.images && product.meal.images[0]"
						class="w-full h-full object-contain"
						:class="{'grayscale': isOutOfStock(product.availability?.id)}"
						:src="String(product.meal.images[0].location)"
						:alt="product.mealNameOffer || ''"
						loading="lazy"
					/>
					<NuxtImg
						v-else
						class="w-full h-full object-contain"
						:class="{'grayscale': outOfStock}"
						src="/images/product-placeholder.jpg"
						alt="Nemáme na skladě"
						loading="lazy"
					/>
				</component>
			</div>
			<LazyOnlineOrderTags v-if="product.meal?.tags?.length" :tags="product.meal.tags" class="absolute right-4 top-4" />

			<LazyOnlineOrderPreferredTag
				v-if="productIsPreferredMenu(product)"
				:product="product"
				:tag-width="57"
				:tag-height="35"
				class="absolute inset-x-0 top-0 p-4"
			/>
		</div>
		<div class="px-2 sm:px-5">
			<component
				:is="hasDetail ? AbsoluteInternalLink : 'div' "
				:to="link"
				class="relative block text-center font-semibold text-body-secondary leading-[1.2rem] mb-2"
			>
				<span class="line-clamp-2" v-text="product.mealNameOffer" />
			</component>
			<div class="flex flex-col gap-2 sm:justify-center">
				<div class="text-body-primary bg-background-secondary typo-body-caption text-center py-1 px-2 rounded-full self-center">
					{{ product.meal?.mealWeight }}
				</div>
				<div
					v-if="product.alergens?.length"
					class="text-body-primary bg-background-secondary typo-body-caption text-center py-1 px-2 rounded-full self-center justify-self-end"
				>
					Alergeny: {{ product.alergens.map(alergen => alergen.alergenDesignation).join() }}
				</div>
			</div>
		</div>
		<div
			class="flex items-center gap-2 p-2 sm:px-5 sm:py-4 relative z-40 self-end flex-wrap"
			:class="[
				isStatic ? 'justify-center' : 'justify-between',
			]"
		>
			<div>
				<div class="transition-all duration-300 max-sm:pl-1">
					<PriceBoxDefault v-if="product.price?.price != null" :price="product.price.price" />
				</div>
			</div>
			<QuantityControls
				v-if="!isStatic && !outOfStock"
				:product="product"
				@change="(type: string) => handleStatusMessage(type)"
			/>
		</div>
		<div class="sr-only" aria-live="polite">
			{{ ariaStatusMessage }}
		</div>
	</div>
</template>

<script setup lang="ts">
	import {useRouteParams} from '@vueuse/router';
	import {productIsPreferredMenu} from '~/utils/productIsPreferredMenu';
	import {isOutOfStock} from '~/utils';
	import type {OfferResult} from '~/openapi';

	type Props = {
		isStatic?: boolean;
		product: OfferResult;
		hasDetail?: boolean;
		isMenuTile?: boolean;
	};

	const props = defineProps<Props>();

	const branch = useRouteParams<string>('branch');
	const slug = useRouteParams<string>('slug');
	const AbsoluteInternalLink = resolveComponent('AbsoluteInternalLink');
	const link = computed(() => props.hasDetail ? `/${branch.value}/online-objednavka/${slug.value}/produkt-${props.product.offerId}` : null);
	const outOfStock = computed(() => isOutOfStock(props.product.availability?.id));

	const ariaStatusMessage = useAriaStatusMessage('');

	function handleStatusMessage(type: string) {
		switch (type) {
			case 'add':
				ariaStatusMessage.value = 'Produkt byl přidán do nákupního košíku.';
				break;
			case 'remove':
				ariaStatusMessage.value = 'Produkt byl odebrán z nákupního košíku.';
				break;
		}
	}
</script>
